import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import {
  FluffyLine,
  GridOverrides,
  PurpleLine,
  TeamSettingFormSchema,
  TeamSettingSection,
  UpdateScipResponse
} from '@mds/common';
import {
  ButtonComponent,
  GridActionsComponent,
  GridCheckboxButtonComponent,
  GridModule,
  GridNoRowsComponent
} from '@mds/ui';
import { MakersiteService } from '@services/makersite.service';
import { ToastrService } from '@services/toaster.service';
import { GridApi, RowNode } from 'ag-grid-enterprise';
import { CreateProductDialogComponent } from '../../../create-product-dialog/create-product-dialog.component';

@Component({
  selector: 'app-scip-settings',
  standalone: true,
  imports: [GridModule, ButtonComponent, MatDialogModule],
  templateUrl: './scip-settings.component.html',
  styleUrls: ['./scip-settings.component.scss']
})
export class ScipSettingsComponent implements OnInit, OnChanges {
  @Input() section: TeamSettingSection;
  @Input() tabData: any;
  @Input() teamRid: string;
  @Output() reload = new EventEmitter<void>();

  overrides: GridOverrides;
  defaultRow: { [key: string]: any };
  private gridApi: GridApi;
  private makersiteService = inject(MakersiteService);
  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['section'] && !changes['section'].firstChange) {
      this.init();
      this.gridApi?.hideOverlay();
    }
  }

  onGridReady(api: GridApi): void {
    this.gridApi = api;
  }

  init() {
    const header = this.getHeader(this.section.formSchema);
    const rowData = this.getRowData(this.section.lines, this.section.actions);
    this.overrides = {
      columnDefs: header,
      rowData,
      defaultColDef: {
        flex: 1,
        minWidth: 10,
        enableValue: true,
        enablePivot: true,
        sortable: true,
        filter: true,
        resizable: true
      },
      noRowsOverlayComponent: GridNoRowsComponent,
      noRowsOverlayComponentParams: {
        title: 'No data to show in table'
      }
    };
  }

  getHeader(schema: TeamSettingFormSchema[]) {
    const header = [];
    schema.forEach((row) => {
      const field = row.key.replace('.', '$');
      const column = {
        field,
        headerName: row.title,
        minWidth: 50,
        cellRenderer: row.formtype === 'checkbox' ? GridCheckboxButtonComponent : '',
        cellRendererParams: {
          onClick: (params: { value: string; node: RowNode }) => {
            const rowData = params.node.data;
            this.updateSelectedSCIP(rowData, field);
          }
        },
        filter: 'agTextColumnFilter'
      };
      header.push(column);
    });
    const actions = {
      headerName: 'Actions',
      field: 'actions',
      lockPosition: true,
      pinned: 'right',
      width: 84,
      cellRenderer: GridActionsComponent,
      cellRendererParams: {
        label: 'Delete',
        onClick: (params: { event: string; node: RowNode }) => {
          const rid = params?.node?.data.out_scip_legal_entity$rid ?? '';
          this.removeLegalEntity(rid);
        }
      }
    };
    header.push(actions);
    return header;
  }

  getRowData(lines: (PurpleLine[] | FluffyLine)[], actions: { delete: boolean }) {
    const rowData = [];
    lines.forEach((line) => {
      const row: { [key: string]: any } = {};
      row.actions = { delete: actions.delete };
      if (Array.isArray(line)) {
        line.forEach((data) => {
          row[data.key.replace('.', '$')] = data.key.endsWith('.rid') ? data.rid : data.display_value;
        });
      }
      if (row.out_scip_legal_entity$default === true) {
        this.defaultRow = row;
      }
      rowData.push(row);
    });
    return rowData;
  }

  createLegalEntity() {
    const dialogRef = this.dialog.open(CreateProductDialogComponent, {
      autoFocus: false,
      data: {
        dataCard: this.tabData,
        redirect: false
      }
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (!data) return;
      this.reload.emit();
    });
  }

  updateSelectedSCIP(row: any, field: string) {
    // there should be at least one out_scip_legal_entity$default
    if (
      this.defaultRow &&
      field === 'out_scip_legal_entity$default' &&
      row.out_scip_legal_entity$rid === this.defaultRow.out_scip_legal_entity$rid
    ) {
      this.reload.emit();
      return;
    }
    const payload = [];
    if (this.defaultRow && field === 'out_scip_legal_entity$default') {
      this.defaultRow.out_scip_legal_entity$default = false;
      payload.push(this.createPayload(this.defaultRow));
      this.defaultRow = row;
    }
    payload.push(this.createPayload(row));
    this.gridApi?.showLoadingOverlay();
    this.makersiteService.submitEditedMetadata(this.teamRid, payload).subscribe((res: UpdateScipResponse) => {
      if (res.fails) {
        this.toastr.showMessage('Update failed', 2000);
      }
      this.toastr.showMessage('Updated Successfully', 2000);
      this.reload.emit();
    });
  }

  createPayload(data: any) {
    const payload: { [key: string]: { [subKey: string]: any } } = {};
    Object.entries(data).forEach(([fullKey, value]) => {
      const [key, subKey] = fullKey.split('$');
      if (!payload[key]) {
        payload[key] = {};
      }

      if (subKey !== 'in') {
        payload[key][subKey] = value;
      }
    });
    return payload;
  }

  private removeLegalEntity(rid: string) {
    this.makersiteService.deleteData(rid).subscribe({
      next: () => {
        this.toastr.showMessage('Deleted Successfully');
        this.reload.emit();
      },
      error: (error) => {
        this.toastr.showMessage(error, 2000);
      }
    });
  }
}
